import { useNavigation } from "@react-navigation/native"
import { StackNavigationProp } from "@react-navigation/stack"
import { Body2, Button, generateShadow, Subtitle1, useTheme, VectorIcon } from "capsule"
import React, { FC, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { StyleSheet, View } from "react-native"

import { PractitionerHomeNS } from "../../features/i18n/constants"
import { ICodesParamList } from "../MainScreen/MainTabs"

type NavigationProp = StackNavigationProp<ICodesParamList>

const ICON_SIZE = 80
const WARNING_ORANGE = "#FF9800"

const PrescriptionLCAIneligibleScreen: FC = () => {
  const {
    fontMaker,
    colors: {
      primary,
      surface: { background },
      white: { highEmphasis: white },
    },
    dimensions: { spacing },
    screenStyle,
  } = useTheme()
  const { t } = useTranslation()
  const navigation = useNavigation<NavigationProp>()

  const onYes = useCallback(() => {
    navigation.navigate("PrescriptionLCAEligibility")
  }, [navigation])

  const onNo = useCallback(() => {
    navigation.navigate("Codes_Main")
  }, [navigation])

  const s = useMemo(
    () => ({
      container: [
        styles.container,
        {
          backgroundColor: background,
          paddingHorizontal: spacing * 1.5,
          paddingTop: spacing,
          paddingBottom: spacing * 1.5,
        },
      ],
      iconContainer: [
        styles.iconContainer,
        {
          marginBottom: spacing * 1.5,
        },
      ],
      description: [
        fontMaker({ weight: "Bold" }),
        {
          marginBottom: spacing,
          textAlign: "center" as const,
        },
      ],
      card: [
        styles.card,
        {
          backgroundColor: white,
          padding: spacing * 1.25,
          borderRadius: 8,
          ...generateShadow(1),
        },
      ],
      boldText: [
        fontMaker({ weight: "Bold" }),
        {
          marginBottom: spacing * 0.75,
          textAlign: "center" as const,
        },
      ],
      normalText: {
        marginBottom: spacing,
        textAlign: "center" as const,
      },
      questionText: {
        marginBottom: spacing * 1.5,
        textAlign: "center" as const,
      },
      buttonsContainer: [
        styles.buttonsContainer,
        {
          marginTop: spacing,
        },
      ],
      button: {
        flex: 1,
      },
      buttonNo: {
        flex: 1,
        borderColor: primary,
      },
      buttonNoText: {
        color: primary,
      },
      buttonSpacing: {
        width: spacing,
      },
    }),
    [background, spacing, white, fontMaker, primary],
  )

  return (
    <View style={[s.container, screenStyle]}>
      <View style={s.iconContainer}>
        <VectorIcon
          name="warning"
          category="MaterialIcons"
          size={ICON_SIZE}
          color={WARNING_ORANGE}
        />
      </View>
      <Subtitle1 style={s.description}>
        {t(`${PractitionerHomeNS}:prescriptionLCAIneligible.description`)}
      </Subtitle1>
      <View style={s.card}>
        <Body2 style={s.boldText}>
          {t(`${PractitionerHomeNS}:prescriptionLCAIneligible.card.boldText`)}
        </Body2>
        <Body2 style={s.normalText}>
          {t(`${PractitionerHomeNS}:prescriptionLCAIneligible.card.normalText`)}
        </Body2>
        <Body2 style={s.questionText}>
          {t(`${PractitionerHomeNS}:prescriptionLCAIneligible.card.question`)}
        </Body2>
        <View style={s.buttonsContainer}>
          <Button mode="contained" onPress={onYes} style={s.button}>
            {t("button.yes")}
          </Button>
          <View style={s.buttonSpacing} />
          <Button mode="outlined" onPress={onNo} style={s.buttonNo} labelStyle={s.buttonNoText}>
            {t("button.no")}
          </Button>
        </View>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
  },
  iconContainer: {
    alignItems: "center",
  },
  card: {
    width: "100%",
  },
  buttonsContainer: {
    flexDirection: "row",
    width: "100%",
  },
})

export default PrescriptionLCAIneligibleScreen
